import { ProjectFormatSchema, type ProjectFormat } from "@scenix/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { RecoveryCountdown } from "../components/RecoveryCountdown";
import { SpaceAvatar } from "../components/SpaceAvatar";
import { ApiError, api } from "../lib/api";
import { chainComplete, chainLabel } from "../lib/chain";
import { RECOVERY_WINDOW_DAYS } from "../lib/recovery";

/** Human labels for the format values the API accepts. */
const FORMAT_LABELS: Record<ProjectFormat, string> = {
  short: "Short",
  long_form: "Long form",
  film: "Film",
  series_episode: "Series episode",
};

export function SpaceRoute() {
  const { spaceId = "" } = useParams();
  const queryClient = useQueryClient();

  const [name, setName] = useState("");
  const [format, setFormat] = useState<ProjectFormat>("short");
  const [composing, setComposing] = useState(false);

  const space = useQuery({
    queryKey: ["space", spaceId],
    queryFn: () => api.getSpace(spaceId as never),
  });

  const projects = useQuery({
    queryKey: ["projects", spaceId],
    queryFn: () => api.listProjects(spaceId as never),
  });

  // The other side of the recovery window, on this page rather than behind a
  // screen of its own: a space holds a handful of projects, and a deleted one is
  // only findable from the list it left (ADR 000078).
  //
  // Not asked for at all by a viewer, who may read it and could never act on it.
  const deletedProjects = useQuery({
    queryKey: ["projects", spaceId, "deleted"],
    queryFn: () => api.listProjects(spaceId as never, { deleted: true }),
    enabled: space.data ? space.data.role !== "viewer" : false,
  });

  // The retired ones, which are the finished work rather than the abandoned
  // work. Asked for by a viewer too: an archived project is readable by
  // anybody who could read it before, and it is what somebody with read access
  // most often comes looking for (ADR 000079).
  const archivedProjects = useQuery({
    queryKey: ["projects", spaceId, "archived"],
    queryFn: () => api.listProjects(spaceId as never, { archived: true }),
  });

  const restore = useMutation({
    mutationFn: (projectId: string) => api.restoreProject(spaceId as never, projectId as never),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["projects", spaceId] });
    },
  });

  const unarchive = useMutation({
    mutationFn: (projectId: string) =>
      api.updateProject(spaceId as never, projectId as never, { status: "active" }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["projects", spaceId] });
    },
  });

  // Counted for the entry tiles, so a space says how much is in it.
  const assets = useQuery({
    queryKey: ["assets", spaceId, ""],
    queryFn: () => api.listAssets(spaceId as never),
  });

  const create = useMutation({
    mutationFn: () => api.createProject(spaceId as never, { name: name.trim(), format }),
    onSuccess: async () => {
      setName("");
      setComposing(false);
      await queryClient.invalidateQueries({ queryKey: ["projects", spaceId] });
    },
  });

  if (space.isPending) return <p role="status">Loading…</p>;

  if (space.error) {
    const missing = space.error instanceof ApiError && space.error.isNotFound;
    return (
      <section className="empty">
        <h1>{missing ? "No such space" : "Could not load this space"}</h1>
        <p className="muted">
          {missing
            ? "It may have been deleted, or you may not be a member of it."
            : space.error.message}
        </p>
        <p>
          <Link to="/spaces">Back to your spaces</Link>
        </p>
      </section>
    );
  }

  // Viewers can read a space but not add to it, so the form is not offered.
  const mayCreate = space.data.role !== "viewer";
  // A logo and a name are the space's own identity, not a project-level
  // decision, so they need the same admin role settings do (ADR 000036's
  // governance line, ADR 000050).
  const mayEditIdentity = space.data.role === "admin" || space.data.role === "owner";
  // Removal is the owner's alone. An admin runs the space; an owner is whose it
  // is, and removal ends the thing everybody else's work hangs beneath
  // (ADR 000062).
  const mayRemove = space.data.role === "owner";

  return (
    <section>
      <header className="page-head">
        <p className="crumb">
          <Link to="/spaces">Spaces</Link>
        </p>
        <div className="space-head-title">
          <SpaceAvatar space={space.data} size={40} />
          <div>
            <h1>{space.data.name}</h1>
            <p className="muted">
              /{space.data.slug} · you are {space.data.role} ·{" "}
              <Link to={`/spaces/${spaceId}/members`}>members</Link> ·{" "}
              <Link to={`/spaces/${spaceId}/settings`}>settings</Link> ·{" "}
              <Link to={`/spaces/${spaceId}/keys`}>keys</Link> ·{" "}
              <Link to={`/spaces/${spaceId}/connect`}>connect</Link>
            </p>
          </div>
        </div>
        {mayEditIdentity && (
          <div className="space-identity-controls">
            <SpaceLogoControl spaceId={space.data.id} hasLogo={Boolean(space.data.logoPath)} />
            <SpaceRenameControl spaceId={space.data.id} currentName={space.data.name} />
            {mayRemove && (
              <SpaceRemoveControl spaceId={space.data.id} currentName={space.data.name} />
            )}
          </div>
        )}
      </header>

      {/*
       * The two halves of a space, as the first thing you see. The material it
       * draws on and the things being made from it (ADR 000008), each saying
       * how much is in it, so the page answers "what is here" before it asks
       * for anything.
       */}
      <div className="space-entries">
        <Link to={`/spaces/${spaceId}/content`} className="entry">
          <span className="entry-count">{assets.data?.length ?? "—"}</span>
          <span className="entry-body">
            <strong>Content</strong>
            <span className="muted">
              Footage, stills, audio, research and links this space draws on.
            </span>
          </span>
        </Link>

        <div className="entry is-static">
          <span className="entry-count">{projects.data?.length ?? "—"}</span>
          <span className="entry-body">
            <strong>Projects</strong>
            <span className="muted">Things being made from it.</span>
          </span>
        </div>
      </div>

      <div className="section-bar">
        <h2 className="section-head">Projects</h2>
        {mayCreate && projects.data && projects.data.length > 0 && (
          <button type="button" className="ghost" onClick={() => setComposing((was) => !was)}>
            {composing ? "Cancel" : "New project"}
          </button>
        )}
      </div>

      {create.error && (
        <p role="alert" className="error">
          {create.error instanceof ApiError ? create.error.userMessage : create.error.message}
        </p>
      )}

      {projects.isPending && <p role="status">Loading projects…</p>}

      {/*
       * The form is an action rather than furniture: shown when a space is
       * empty, because that is the only thing to do, and behind a button once
       * there are projects, because then the list is what you came for.
       */}
      {mayCreate && (composing || projects.data?.length === 0) && (
        <form
          className="compose"
          onSubmit={(event) => {
            event.preventDefault();
            if (name.trim()) create.mutate();
          }}
        >
          {projects.data?.length === 0 && (
            <>
              <h3>Start the first project</h3>
              <p className="muted">
                It begins at the treatment: one paragraph saying what this is and who it is for.
                Everything else follows from that.
              </p>
            </>
          )}
          <div className="compose-row">
            <input
              aria-label="New project name"
              placeholder="Name a new project"
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={300}
              autoFocus={composing}
            />
            <select
              aria-label="Format"
              value={format}
              onChange={(event) => setFormat(event.target.value as ProjectFormat)}
            >
              {ProjectFormatSchema.options.map((value) => (
                <option key={value} value={value}>
                  {FORMAT_LABELS[value]}
                </option>
              ))}
            </select>
            <button type="submit" disabled={!name.trim() || create.isPending}>
              {create.isPending ? "Creating…" : "Create project"}
            </button>
          </div>
        </form>
      )}

      {!mayCreate && projects.data?.length === 0 && (
        <div className="empty-inline">
          <p>Nothing here yet. An editor can create the first one.</p>
        </div>
      )}

      {projects.data && projects.data.length > 0 && (
        <ul className="cards">
          {projects.data.map((project) => (
            <li key={project.id}>
              <Link to={`/spaces/${spaceId}/projects/${project.id}`}>
                <h3>{project.name}</h3>
                {/*
                 * How far along, read from the chain rather than from the word
                 * somebody last chose. Nothing maintains the stage, so a card
                 * printing it would say treatment beside a finished cut
                 * (ADR 000067).
                 */}
                <p className="project-meta">
                  <span className={`chain-read${chainComplete(project.chain) ? " is-made" : ""}`}>
                    {chainLabel(project.chain)}
                  </span>
                  {FORMAT_LABELS[project.format]} · {project.status}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {/*
       * The retired projects, in the product rather than only at an address
       * somebody kept. No clock beside them: archiving loses nothing and
       * expires never, which is the whole difference from the section below
       * (ADR 000079).
       */}
      {archivedProjects.data && archivedProjects.data.length > 0 && (
        <>
          <div className="section-bar">
            <h2 className="section-head">Archived</h2>
          </div>
          <p className="muted">
            Retired from the list above, and otherwise untouched: each keeps its treatment,
            timeline, notes and thread, and opens exactly as it did.
          </p>
          {unarchive.error && (
            <p role="alert" className="error">
              {unarchive.error instanceof ApiError
                ? unarchive.error.userMessage
                : unarchive.error.message}
            </p>
          )}
          <ul className="corpus">
            {archivedProjects.data.map((project) => (
              <li key={project.id} className="corpus-item">
                <div className="corpus-item-row">
                  <Link className="corpus-body" to={`/spaces/${spaceId}/projects/${project.id}`}>
                    <strong>{project.name}</strong>
                    <span className="corpus-meta">
                      {FORMAT_LABELS[project.format]} · {chainLabel(project.chain)}
                    </span>
                  </Link>
                  {mayCreate && (
                    <button
                      type="button"
                      disabled={unarchive.isPending}
                      onClick={() => unarchive.mutate(project.id)}
                    >
                      Unarchive
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {/*
       * Only when there is something in it. A heading that says "nothing
       * deleted" on every space is a permanent reminder of a thing that has not
       * happened.
       */}
      {mayCreate && deletedProjects.data && deletedProjects.data.length > 0 && (
        <>
          <div className="section-bar">
            <h2 className="section-head">Deleted</h2>
          </div>
          <p className="muted">
            Restore brings a project back exactly as it was, with its treatment, timeline, notes and
            thread, for {RECOVERY_WINDOW_DAYS} days after it was deleted. After that it is removed
            for good and nothing here can bring it back.
          </p>
          {restore.error && (
            <p role="alert" className="error">
              {restore.error instanceof ApiError
                ? restore.error.userMessage
                : restore.error.message}
            </p>
          )}
          <ul className="corpus">
            {deletedProjects.data.map((project) => (
              <li key={project.id} className="corpus-item">
                <div className="corpus-item-row">
                  <span className="corpus-body">
                    <strong>{project.name}</strong>
                    <span className="corpus-meta">
                      {FORMAT_LABELS[project.format]}
                      {project.deletedAt &&
                        ` · deleted ${new Date(project.deletedAt).toLocaleDateString()}`}
                    </span>
                    <RecoveryCountdown deletedAt={project.deletedAt} />
                  </span>
                  <button
                    type="button"
                    disabled={restore.isPending}
                    onClick={() => restore.mutate(project.id)}
                  >
                    Restore
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

/**
 * Rename a space. Admin and owner only, and only from here: no agent key and no
 * MCP client can rename a space at all (ADR 000050).
 *
 * The new name is typed rather than edited in place, and the current name has to
 * be typed back before Rename does anything. A space's name is on every screen
 * somebody uses to recognise their own footage, so the gate is deliberately one
 * that cannot be passed absent-mindedly, which a confirm dialog can be.
 */
function SpaceRenameControl({ spaceId, currentName }: { spaceId: string; currentName: string }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [next, setNext] = useState("");
  const [confirmation, setConfirmation] = useState("");

  const rename = useMutation({
    mutationFn: () => api.renameSpace(spaceId as never, next.trim()),
    onSuccess: async () => {
      setOpen(false);
      setNext("");
      setConfirmation("");
      // The space's own page and the listing that shows its name both go stale.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["space", spaceId] }),
        queryClient.invalidateQueries({ queryKey: ["spaces"] }),
      ]);
    },
  });

  const named = next.trim() !== "";
  // Exact, including case: a confirmation that accepts a near miss is not asking
  // somebody to read what they are renaming.
  const confirmed = confirmation === currentName;

  if (!open) {
    return (
      <button type="button" className="ghost" onClick={() => setOpen(true)}>
        Rename
      </button>
    );
  }

  return (
    <form
      className="space-rename"
      onSubmit={(event) => {
        event.preventDefault();
        if (named && confirmed) rename.mutate();
      }}
    >
      <input
        aria-label="New space name"
        placeholder="A new name for this space"
        value={next}
        onChange={(event) => setNext(event.target.value)}
        maxLength={200}
      />
      <input
        aria-label={`Type ${currentName} to confirm`}
        placeholder={`Type ${currentName} to confirm`}
        value={confirmation}
        onChange={(event) => setConfirmation(event.target.value)}
      />
      <button type="submit" disabled={!named || !confirmed || rename.isPending}>
        {rename.isPending ? "Renaming…" : "Rename"}
      </button>
      <button
        type="button"
        className="ghost"
        onClick={() => {
          setOpen(false);
          setNext("");
          setConfirmation("");
        }}
      >
        Cancel
      </button>
      {named && confirmation !== "" && !confirmed && (
        <p className="muted">That is not this space's current name, so nothing has changed.</p>
      )}
      {rename.error && (
        <p role="alert" className="error">
          {rename.error instanceof ApiError ? rename.error.userMessage : rename.error.message}
        </p>
      )}
    </form>
  );
}

/**
 * Remove a space. Owner only, and only from here (ADR 000050, ADR 000062).
 *
 * The same typed confirmation as a rename, for more reason: this is the widest
 * thing anybody can do in the product. It is a gate that cannot be passed
 * absent-mindedly, which is exactly what a confirm dialog can be.
 *
 * The copy says what actually happens, because "are you sure?" is not an
 * answer to the question somebody is really asking, which is whether the
 * footage is gone.
 */
function SpaceRemoveControl({ spaceId, currentName }: { spaceId: string; currentName: string }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirmation, setConfirmation] = useState("");

  const remove = useMutation({
    mutationFn: () => api.removeSpace(spaceId as never),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["spaces"] });
      // Off this page, because it is no longer reachable by anybody including
      // the person who just left it.
      navigate("/spaces");
    },
  });

  const confirmed = confirmation === currentName;

  if (!open) {
    return (
      <button type="button" className="ghost danger" onClick={() => setOpen(true)}>
        Remove space
      </button>
    );
  }

  return (
    <form
      className="space-remove"
      onSubmit={(event) => {
        event.preventDefault();
        if (confirmed) remove.mutate();
      }}
    >
      <p>
        Removing {currentName} takes its projects, footage, notes, keys and share links with it.
        Nothing is destroyed today: you have 30 days to put it back, whole, from the Spaces page.
      </p>
      <input
        aria-label={`Type ${currentName} to confirm removal`}
        placeholder={`Type ${currentName} to confirm`}
        value={confirmation}
        onChange={(event) => setConfirmation(event.target.value)}
      />
      <button type="submit" className="danger" disabled={!confirmed || remove.isPending}>
        {remove.isPending ? "Removing…" : "Remove space"}
      </button>
      <button
        type="button"
        className="ghost"
        onClick={() => {
          setOpen(false);
          setConfirmation("");
        }}
      >
        Cancel
      </button>
      {confirmation !== "" && !confirmed && (
        <p className="muted">That is not this space's name, so nothing has changed.</p>
      )}
      {remove.error && (
        <p role="alert" className="error">
          {remove.error instanceof ApiError ? remove.error.userMessage : remove.error.message}
        </p>
      )}
    </form>
  );
}

/** Upload, replace or remove a space's logo. Admin and owner only. */
function SpaceLogoControl({ spaceId, hasLogo }: { spaceId: string; hasLogo: boolean }) {
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["space", spaceId] });

  const upload = useMutation({
    mutationFn: (file: File) => api.setSpaceLogo(spaceId as never, file),
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: () => api.deleteSpaceLogo(spaceId as never),
    onSuccess: refresh,
  });

  const error = upload.error ?? remove.error;

  return (
    <div className="space-logo-control">
      <input
        ref={fileInput}
        type="file"
        accept="image/png,image/jpeg,image/gif"
        className="visually-hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (file) upload.mutate(file);
        }}
      />
      <button type="button" className="ghost" onClick={() => fileInput.current?.click()}>
        {upload.isPending ? "Uploading…" : hasLogo ? "Change logo" : "Add a logo"}
      </button>
      {hasLogo && (
        <button
          type="button"
          className="ghost"
          onClick={() => {
            if (confirm("Remove this space's logo?")) remove.mutate();
          }}
        >
          {remove.isPending ? "Removing…" : "Remove logo"}
        </button>
      )}
      {error && (
        <p role="alert" className="error">
          {error instanceof ApiError ? error.userMessage : error.message}
        </p>
      )}
    </div>
  );
}
